import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Save, Stethoscope, Activity, HeartPulse } from "lucide-react";

const GENERAL_ITEMS = [
  ["pallor", "Pallor"],
  ["icterus", "Icterus"],
  ["cyanosis", "Cyanosis"],
  ["clubbing", "Clubbing"],
  ["lymphadenopathy", "Significant lymphadenopathy"],
];
const STATUSES = ["Not examined", "Absent", "Present"];
const SYSTEMIC = [
  ["cvs", "Cardiovascular (CVS)"],
  ["respiratory", "Respiratory system"],
  ["cns", "Central nervous system"],
  ["abdomen", "Abdominal examination"],
];
const VITALS = [
  ["pulse", "Pulse", "e.g. 82 /min, regular"],
  ["bp", "Blood pressure", "e.g. 126/80 mmHg"],
  ["resp_rate", "Respiratory rate", "e.g. 16 /min"],
  ["temp", "Temperature", "e.g. 98.6 °F / 37 °C"],
  ["consciousness", "Consciousness", "e.g. Alert & oriented / GCS 15"],
  ["urine_output", "Urine output", "e.g. Adequate / 1200 mL/day"],
];

const emptyExam = () => ({
  height: "", weight: "", build: "",
  vitals: { pulse: "", bp: "", resp_rate: "", temp: "", consciousness: "", urine_output: "" },
  general: GENERAL_ITEMS.reduce((a, [k]) => ({ ...a, [k]: { status: "Not examined", note: "" } }), {}),
  breast: "",
  systemic: { cvs: "", respiratory: "", cns: "", abdomen: "" },
});

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <h2 className="mb-3 flex items-center gap-2 font-heading text-xl font-semibold text-foreground">
        <Icon className="h-5 w-5 text-primary" /> {title}
      </h2>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

const statusStyle = {
  Present: "bg-destructive text-destructive-foreground border-destructive",
  Absent: "bg-primary text-primary-foreground border-primary",
  "Not examined": "bg-muted text-muted-foreground border-border",
};

export default function DoctorFindings() {
  const { role, caseId } = useParams();
  const navigate = useNavigate();
  const isDoctor = role === "doctor";
  const [c, setC] = useState(null);
  const [exam, setExam] = useState(emptyExam());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getCase(caseId).then((data) => {
      setC(data);
      const base = emptyExam();
      const e = data.exam || {};
      setExam({
        ...base, ...e,
        vitals: { ...base.vitals, ...(e.vitals || {}) },
        general: { ...base.general, ...(e.general || {}) },
        systemic: { ...base.systemic, ...(e.systemic || {}) },
      });
    }).catch(() => navigate(`/${role}/cases`));
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      setC(await api.override(caseId, "exam", exam));
      toast.success("Findings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save findings");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setExam({ ...exam, [k]: v });
  const setVital = (k, v) => setExam({ ...exam, vitals: { ...exam.vitals, [k]: v } });
  const setGeneral = (k, patch) => setExam({ ...exam, general: { ...exam.general, [k]: { ...exam.general[k], ...patch } } });
  const setSystemic = (k, v) => setExam({ ...exam, systemic: { ...exam.systemic, [k]: v } });

  if (!c) {
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const ro = !isDoctor;

  return (
    <div className="min-h-screen px-6 pb-28 pt-8">
      <div className="mx-auto max-w-2xl">
        <button data-testid="findings-back" onClick={() => navigate(`/${role}/cases/${caseId}`)}
          className="mb-5 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4" /> Back to case
        </button>

        <div className="mb-6">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">Doctor's findings</p>
          <h1 className="font-heading text-3xl font-semibold text-foreground sm:text-4xl">{c.patient?.name}</h1>
          <p className="text-sm text-muted-foreground">Clinical examination — fed into the assistant's reasoning.</p>
        </div>

        <Card className="flex flex-col gap-8 p-7" data-testid="findings-form">
          <Section icon={Activity} title="Anthropometry">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-1.5"><Label>Height</Label>
                <Input data-testid="exam-height" disabled={ro} value={exam.height} onChange={(e) => setField("height", e.target.value)} placeholder="e.g. 162 cm" /></div>
              <div className="flex flex-col gap-1.5"><Label>Weight</Label>
                <Input data-testid="exam-weight" disabled={ro} value={exam.weight} onChange={(e) => setField("weight", e.target.value)} placeholder="e.g. 68 kg" /></div>
              <div className="flex flex-col gap-1.5"><Label>Build</Label>
                <Input data-testid="exam-build" disabled={ro} value={exam.build} onChange={(e) => setField("build", e.target.value)} placeholder="e.g. Average" /></div>
            </div>
          </Section>

          <Section icon={HeartPulse} title="Vital signs">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {VITALS.map(([k, label, ph]) => (
                <div key={k} className="flex flex-col gap-1.5">
                  <Label>{label}</Label>
                  <Input data-testid={`vital-${k}`} disabled={ro} value={exam.vitals[k]} onChange={(e) => setVital(k, e.target.value)} placeholder={ph} />
                </div>
              ))}
            </div>
          </Section>

          <Section icon={Stethoscope} title="General physical examination">
            {GENERAL_ITEMS.map(([k, label]) => (
              <div key={k} className="rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">{label}</span>
                  <div className="flex gap-1">
                    {STATUSES.map((s) => (
                      <button key={s} data-testid={`general-${k}-${s.replace(/\s+/g, "-").toLowerCase()}`}
                        disabled={ro}
                        onClick={() => setGeneral(k, { status: s })}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors disabled:opacity-60 ${
                          exam.general[k].status === s ? statusStyle[s] : "border-border bg-card text-muted-foreground hover:bg-muted"
                        }`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                {exam.general[k].status === "Present" && (
                  <Input className="mt-2" data-testid={`general-${k}-note`} disabled={ro}
                    value={exam.general[k].note} onChange={(e) => setGeneral(k, { note: e.target.value })}
                    placeholder="Describe findings (site, size, character…)" />
                )}
              </div>
            ))}
          </Section>

          <Section icon={Stethoscope} title="Breast examination findings">
            <Textarea data-testid="exam-breast" disabled={ro} rows={4} value={exam.breast}
              onChange={(e) => setField("breast", e.target.value)}
              placeholder="Inspection & palpation: lump location/size/consistency/mobility, skin changes, nipple, axillary nodes…" />
          </Section>

          <Section icon={Stethoscope} title="Other systemic examination">
            {SYSTEMIC.map(([k, label]) => (
              <div key={k} className="flex flex-col gap-1.5">
                <Label>{label}</Label>
                <Textarea data-testid={`systemic-${k}`} disabled={ro} rows={2} value={exam.systemic[k]}
                  onChange={(e) => setSystemic(k, e.target.value)} placeholder={`${label} findings…`} />
              </div>
            ))}
          </Section>

          {isDoctor ? (
            <Button data-testid="save-findings-btn" onClick={save} disabled={saving} className="gap-2 self-start">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving…" : "Save findings"}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">These findings are recorded by your clinician.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
